/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/*
 * Normalise supplier photography without changing the source files.
 * Several images contain large baked-in white margins, so object-fit alone
 * cannot centre the visible product. A small canvas sample finds the actual
 * subject bounds and sizes that subject consistently inside its image stage.
 */
const productImageSelector = [
  '.uis24-product-gallery .product__media img',
].join(',');

function findVisibleImageBounds(image) {
  const longestSide = 128;
  const scale = Math.min(longestSide / image.naturalWidth, longestSide / image.naturalHeight, 1);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });

  canvas.width = width;
  canvas.height = height;
  context.drawImage(image, 0, 0, width, height);

  let pixels;
  try {
    pixels = context.getImageData(0, 0, width, height).data;
  } catch (_) {
    return null;
  }

  const corners = [0, width - 1, (height - 1) * width, height * width - 1];
  const background = corners.reduce(
    (sum, pixelIndex) => {
      const offset = pixelIndex * 4;
      sum[0] += pixels[offset];
      sum[1] += pixels[offset + 1];
      sum[2] += pixels[offset + 2];
      return sum;
    },
    [0, 0, 0],
  ).map((value) => value / corners.length);

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const alpha = pixels[offset + 3];
      const distance = Math.max(
        Math.abs(pixels[offset] - background[0]),
        Math.abs(pixels[offset + 1] - background[1]),
        Math.abs(pixels[offset + 2] - background[2]),
      );

      if (alpha > 20 && distance > 22) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) return null;

  const paddingX = Math.max(2, Math.round((maxX - minX + 1) * 0.06));
  const paddingY = Math.max(2, Math.round((maxY - minY + 1) * 0.06));
  minX = Math.max(0, minX - paddingX);
  minY = Math.max(0, minY - paddingY);
  maxX = Math.min(width - 1, maxX + paddingX);
  maxY = Math.min(height - 1, maxY + paddingY);

  return {
    x: minX / scale,
    y: minY / scale,
    width: (maxX - minX + 1) / scale,
    height: (maxY - minY + 1) / scale,
  };
}

function normaliseProductImage(image) {
  if (image.dataset.uis24Normalised === image.currentSrc || !image.naturalWidth || !image.naturalHeight) return;

  const bounds = findVisibleImageBounds(image);
  if (!bounds) return;

  const stage = image.closest(
    '.uis24-product-card__media-link, .product__media, .uis24-product-recommendation__media, .card__media',
  ) || image.parentElement;
  const stageRect = stage.getBoundingClientRect();
  const stageWidth = Math.max(stageRect.width, 1);
  const stageHeight = Math.max(stageRect.height, 1);
  const targetFill = image.closest('.uis24-product-gallery') ? 76 : 80;
  const scaleToStage = Math.min(
    (stageWidth * targetFill) / (100 * bounds.width),
    (stageHeight * targetFill) / (100 * bounds.height),
  );
  const width = (image.naturalWidth * scaleToStage * 100) / stageWidth;
  const height = (image.naturalHeight * scaleToStage * 100) / stageHeight;
  const subjectCenterX = bounds.x + bounds.width / 2;
  const subjectCenterY = bounds.y + bounds.height / 2;
  const left = 50 - (subjectCenterX * scaleToStage * 100) / stageWidth;
  const top = 50 - (subjectCenterY * scaleToStage * 100) / stageHeight;

  stage.style.setProperty('position', 'relative', 'important');
  stage.style.setProperty('overflow', 'hidden', 'important');
  stage.style.setProperty('background', '#ffffff', 'important');
  image.style.setProperty('position', 'absolute', 'important');
  image.style.setProperty('inset', 'auto', 'important');
  image.style.setProperty('width', `${width}%`, 'important');
  image.style.setProperty('height', `${height}%`, 'important');
  image.style.setProperty('max-width', 'none', 'important');
  image.style.setProperty('max-height', 'none', 'important');
  image.style.setProperty('left', `${left}%`, 'important');
  image.style.setProperty('top', `${top}%`, 'important');
  image.style.setProperty('transform', 'none', 'important');
  image.style.setProperty('object-fit', 'fill', 'important');
  image.style.setProperty('object-position', 'center', 'important');
  image.dataset.uis24Normalised = image.currentSrc;
}

function normaliseProductImages(root = document) {
  root.querySelectorAll(productImageSelector).forEach((image) => {
    if (image.complete) normaliseProductImage(image);
    else image.addEventListener('load', () => normaliseProductImage(image), { once: true });
  });
}

document.addEventListener('DOMContentLoaded', () => normaliseProductImages(), { once: true });
new MutationObserver((mutations) => {
  mutations.forEach(({ addedNodes }) => {
    addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches?.(productImageSelector)) normaliseProductImage(node);
      normaliseProductImages(node);
    });
  });
}).observe(document.documentElement, { childList: true, subtree: true });
